import React, { useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { get_xcsrf_token } from "../../core/auth";
import { useEmailDataContext } from "../../partials/dataContext";
import Progress from "../../partials/Progress";
import MessageBox from "../../partials/MessageBox";
import isLoggedIn from "../../core/isLoggedIn";
import getUser from "../../core/getUser";
import { useDispatch, useSelector } from "react-redux";
import ScreenRouting from "../../partials/screenRouting";
import { useErrorContext } from "../../partials/errorContext";
import { useScreenContext } from "../../partials/screenContext";
import LoadingSpinner from "../../partials/loading";
import { getOnlineNode } from "../../core/getNode";

/* global require */

const App = ({ title, isOfficeInitialized }) => {
  const { initLogout } = useScreenContext();
  const { setSubscriber } = useEmailDataContext();
  const { isLoading, setLoading, isError, setError, errorMessage, setErrorMessage } = useErrorContext();

  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer.user);
  const token = useSelector((state) => state.tokenReducer);
  const csrf = useSelector((state) => state.csrfTokenReducer);
  const system = useSelector((state) => state.systemReducer);

  // hooks
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);

  // Check system first
  useEffect(() => {
    checkStorage();
  }, []);

  useEffect(() => {
    if (system.hasOwnProperty("subdomain")) {
      get_xcsrf_token()
        .then((response) => {
          dispatch({ type: "SAVE_CSRF", payload: response.data });
          checkLogin();
        })
        .catch(() => {
          checkLogin();
        });
    }
  }, [system]);

  useEffect(() => {
    if (user && user?.current_user) {
      checkPermissions();
      get_xcsrf_token().then((response) => {
        dispatch({ type: "SAVE_CSRF", payload: response.data });
      });
    }
  }, [user]);

  const checkStorage = () => {
    let _system = localStorage.getItem("system");
    let _user = localStorage.getItem("user");
    let _token = localStorage.getItem("token");
    let _csrf = localStorage.getItem("csrf");
    let _config = localStorage.getItem("config");

    if (_system) {
      _system = JSON.parse(_system);
      dispatch({ type: "SAVE_SYSTEM", payload: _system ? _system : {} });

      if (_user) {
        _user = JSON.parse(_user);
      }

      if (token) {
        _token = JSON.parse(_token);
        dispatch({ type: "SAVE_TOKEN", payload: _token });
      }

      if (_csrf) {
        _csrf = JSON.parse(_csrf);
        dispatch({ type: "SAVE_CSRF", payload: _csrf });
      }

      if (_config) {
        _config = JSON.parse(_config);
        dispatch({ type: "SAVE_CONFIG", payload: _config });
      }

      if (_system && _user && _token && _csrf) {
        const data = {
          user: _user ? _user : {},
          system: _system ? _system : {},
          token: _token ? _token : {},
          csrf: _csrf ? _csrf : {},
        };

        if (
          data &&
          data?.user &&
          data?.user?.current_user &&
          data?.system &&
          data?.system?.subdomain &&
          token?.access_token &&
          data?.csrf
        ) {
          dispatch({ type: "SAVE_SESSION", payload: data.user });
        }
      }
    } else {
      setLoading(false);
      setError(false);
      setErrorMessage("");
    }
  };

  const checkLogin = () => {
    isLoggedIn().then((isLoggedIn) => {
      if (isLoggedIn) {
        getUserDetails();
      } else {
        login();
      }
    });
  };

  const login = () => {
    const code = queryParams.get("code");
    const state = queryParams.get("state");

    const error_response = queryParams.get("error");
    const error_description = queryParams.get("error_description");

    if (code && state) {
      const path = `/openid_connect_rest_auth/authenticate/adfs?code=${code}&state=${state}&debug&redirect_uri=${window.location.origin}${window.location.pathname}`;

      getOnlineNode(path)
        .then((response) => {
          dispatch({ type: "SAVE_SESSION", payload: response.data });
          handleUser();
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message;

          if (errorMessage === "User already logged in") {
            handleUser();
          } else {
            setErrorMessage(errorMessage ? errorMessage : "Unknown error");
            setError(true);
            setLoading(false);
          }
        });
    } else {
      if (error_response) {
        setErrorMessage(error_description ? error_description : "Unknown error");
        setError(true);
      }

      setLoading(false);
    }
  };

  const getUserDetails = async () => {
    const _user = await getUser();
    dispatch({ type: "SAVE_SESSION", payload: _user });
    setLoading(false);
    setError(false);
    setErrorMessage("");
  };

  const handleUser = async () => {
    const user = await getUser();
    dispatch({ type: "SAVE_SESSION", payload: user });
    setLoading(false);
    setError(false);
    setErrorMessage("");
  };

  const checkPermissions = () => {
    if (user.current_user.roles) {
      user.current_user.roles.forEach((role) => {
        if (
          role === "presentation_user" ||
          role === "presentation_manager" ||
          role === "editor" ||
          role === "administrator"
        ) {
          setSubscriber(true);
        } else {
          setSubscriber(false);
        }
      });
    }
  };

  // if (!isOfficeInitialized) {
  //   return (
  //     <Progress
  //       title={title}
  //       logo={require("./../../../assets/ca-app-icon-90x90.png")}
  //       message="Please sideload your addin to see app body."
  //     />
  //   );
  // }

  return (
    <div className="app-wrapper" style={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        <div
          className="loader"
          style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {isError && (
            <div className="message-box-wrapper">
              <MessageBox messageBoxVisible={isError} messageTitle={`Error`} messageBody={errorMessage} />
            </div>
          )}
          <ScreenRouting />
        </>
      )}
    </div>
  );
};

App.propTypes = {
  title: PropTypes.string,
  isOfficeInitialized: PropTypes.bool,
};

export default App;
